import axios from 'axios';

/**
 * Interface representing a Team
 */
export interface Team {
  id: number;
  name: string;
  group_id: number | null;
  logo_url: string;
  rank?: number | null;
  created_at?: string; // Optional as it might not be included in form data
}

/**
 * Interface for team data coming from forms
 */
export interface TeamFormData {
  id: number | string;
  name: string;
  group_id: number | string | null;
  logo_url?: string;
  rank?: number | null;
}

/**
 * Validates and transforms team data to ensure proper types
 * @param data - The input team data
 * @returns Properly typed team object
 */
const createTeamObject = (data: TeamFormData): Team => {
  // Validate required fields
  if (!data.id) {
    throw new Error('Team ID is required');
  }
  if (!data.name) {
    throw new Error('Team name is required');
  }

  return {
    id: typeof data.id === 'string' ? parseInt(data.id, 10) : data.id,
    name: data.name,
    group_id: data.group_id === '' ? null :
              (data.group_id ?
                (typeof data.group_id === 'string' ? parseInt(data.group_id, 10) : data.group_id)
                : null),
    logo_url: data.logo_url || '',
    rank: data.rank ?? null,
  };
};

/**
 * Fetches all teams from the API
 * @returns Array of teams
 */
export const getTeams = async (): Promise<Team[]> => {
  try {
    const response = await axios.get<Team[]>('/api/teams');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching teams:', error);
    return [];
  }
};

/**
 * Fetches a specific team by ID
 * @param id - Team ID
 * @returns Team object
 */
export const getTeam = async (id: number): Promise<Team> => {
  try {
    const response = await axios.get<Team>(`/api/teams/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching team ${id}:`, error);
    throw error;
  }
};

/**
 * Creates a new team
 * @param teamData - Raw team data from form
 * @returns Created team
 */
export const createTeam = async (teamData: TeamFormData): Promise<Team> => {
  try {
    // Create a properly typed team object
    const validTeam = createTeamObject(teamData);
    
    const response = await axios.post<Team>('/api/teams', validTeam);
    return response.data;
  } catch (error) {
    console.error('Error creating team:', error);
    throw error;
  }
};

/**
 * Updates an existing team
 * @param id - Team ID
 * @param teamData - Raw team data from form
 * @returns Updated team
 */
export const updateTeam = async (id: number, teamData: TeamFormData): Promise<Team> => {
  try {
    // Create a properly typed team object
    const validTeam = createTeamObject({
      ...teamData,
      id: typeof id === 'string' ? parseInt(id, 10) : id
    });
    
    const response = await axios.put<Team>(`/api/teams/${id}`, validTeam);
    return response.data;
  } catch (error) {
    console.error(`Error updating team ${id}:`, error);
    throw error;
  }
};

/**
 * Deletes a team
 * @param id - Team ID to delete
 * @returns Response data
 */
/**
 * Fetches teams that have a rank set (the "worst pool"), ordered by rank
 */
export const getRankedTeams = async (): Promise<Team[]> => {
  try {
    const response = await axios.get<Team[]>('/api/teams/ranked');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching ranked teams:', error);
    return [];
  }
};

export const deleteTeam = async (id: number): Promise<any> => {
  try {
    const response = await axios.delete(`/api/teams/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting team ${id}:`, error);
    throw error;
  }
}; 